import { API_BASE } from "./config/api";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom"; // ⬅️ NEW



function toYMD(date) {
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${m}-${d}`;
}

// helper: read current user's admin flag from login
function currentUserIsAdmin() {
  const raw =
    localStorage.getItem("is_admin") ??
    localStorage.getItem("isadmin") ??
    localStorage.getItem("admin") ??
    localStorage.getItem("isAdmin");
  if (raw == null) return false;
  const v = String(raw).trim().toLowerCase();
  return v === "true" || v === "1" || v === "yes" || v === "y";
}

export default function ArnProfileSetup() {
  const navigate = useNavigate(); // ⬅️ NEW

  // ---------- form fields ----------
  const [arnNumber, setArnNumber] = useState("");
  const [euin, setEuin] = useState("");
  const [pan, setPan] = useState("");
  const [dob, setDob] = useState("");
  const [holderName, setHolderName] = useState("");
  const [gmail, setGmail] = useState("");
  const [contact1, setContact1] = useState("");
  const [contact2, setContact2] = useState("");
  const [camsId, setCamsId] = useState("");
  const [camsPwd, setCamsPwd] = useState("");
  const [kfinId, setKfinId] = useState("");
  const [kfinPwd, setKfinPwd] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [remarks, setRemarks] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [confirmLoginPassword, setConfirmLoginPassword] = useState("");
  const [saving, setSaving] = useState(false);

  // NEW: createe’s admin flag (visible only if creator is admin)
  const creatorIsAdmin = useMemo(() => currentUserIsAdmin(), []);
  const [isAdminNew, setIsAdminNew] = useState(false);

  // errors (mobile)
  const [contact1Err, setContact1Err] = useState(false);
  const [contact2Err, setContact2Err] = useState(false);

  // refs
  const nameRef = useRef(null);
  const camsPwdTimer = useRef(null);
  const kfinPwdTimer = useRef(null);

  // ---------- derived constraints ----------
  const today = useMemo(() => new Date(), []);
  const minDOB = useMemo(() => {
    const d = new Date(today);
    d.setFullYear(d.getFullYear() - 90);
    return toYMD(d);
  }, [today]);
  const maxDOB = useMemo(() => {
    const d = new Date(today);
    d.setFullYear(d.getFullYear() - 18);
    return toYMD(d);
  }, [today]);

  useEffect(() => {
    const t = toYMD(today);
    setValidFrom(t);
    setValidTo(t);
    return () => {
      if (camsPwdTimer.current) clearTimeout(camsPwdTimer.current);
      if (kfinPwdTimer.current) clearTimeout(kfinPwdTimer.current);
    };
  }, [today]);

  // ---------- handlers ----------
  const onArnChange = (v) => setArnNumber(v.replace(/\D/g, "").slice(0, 8));
  const onEuinChange = (v) => setEuin(v.replace(/\D/g, "").slice(0, 8));

  const onPanChange = (v) => {
    let value = v.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (value.length <= 5) value = value.replace(/[^A-Z]/g, "");
    else if (value.length <= 9) value = value.slice(0, 5) + value.slice(5).replace(/[^0-9]/g, "");
    else if (value.length === 10) value = value.slice(0, 9) + value[9].replace(/[^A-Z]/g, "");
    setPan(value.slice(0, 10));
  };

  const onHolderNameChange = (v) => setHolderName(v.toUpperCase().replace(/[^A-Z\s]/g, ""));
  const onGmailChange = (v) => {
    setGmail(v);
    if (/^[a-zA-Z0-9._%+-]+@gmail\.com$/.test(v)) setCamsId(v);
  };
  const onContact1Change = (v) => setContact1(v.replace(/\D/g, "").slice(0, 10));
  const onContact2Change = (v) => setContact2(v.replace(/\D/g, "").slice(0, 10));
  const onContactBlur = (which) => (which === 1 ? setContact1Err(contact1.length !== 10) : setContact2Err(contact2.length !== 10));
  const onDobBlur = () => {
    if (!dob) return;
    if (dob < minDOB || dob > maxDOB) {
      alert(`❌ Invalid DOB!\nIt must be between ${minDOB} and ${maxDOB}.`);
      setDob("");
      setTimeout(() => nameRef.current?.focus(), 100);
    } else nameRef.current?.focus();
  };
  const onValidFromBlur = () => {
    const t = toYMD(today);
    if (validFrom < t) setValidFrom(t);
    if (validTo && validTo < validFrom) setValidTo(validFrom);
  };
  const onValidToFocus = () => { if (!validTo) setValidTo(validFrom || toYMD(today)); };
  const onValidToBlur = () => { if (validTo < validFrom) setValidTo(validFrom); };

  const togglePwd = (which) => {
    const el = document.getElementById(which);
    if (!el) return;
    const toText = el.type === "password";
    el.type = toText ? "text" : "password";
    if (toText) {
      const timerRef = which === "cams_password" ? camsPwdTimer : kfinPwdTimer;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const el2 = document.getElementById(which);
        if (el2?.type === "text") el2.type = "password";
      }, 3000);
    }
  };

  // ---------- validity ----------
  const isFormValid = useMemo(() => {
    const panOk = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan);
    const gmailOk = /^[a-zA-Z0-9._%+-]+@gmail\.com$/.test(gmail);
    const arnOk = /^\d{1,8}$/.test(arnNumber);
    const euinOk = /^\d{1,8}$/.test(euin);
    const c1Ok = contact1.length === 10;
    const c2Ok = contact2.length === 10;
    const dobOk = dob && dob >= minDOB && dob <= maxDOB;
    const startOk = !!validFrom && validFrom >= toYMD(today);
    const endOk = !!validTo && validTo >= validFrom;
    const loginPasswordOk = loginPassword.length >= 8 && loginPassword === confirmLoginPassword;

    return (
      arnOk &&
      euinOk &&
      panOk &&
      dobOk &&
      holderName.trim().length > 0 &&
      gmailOk &&
      c1Ok &&
      c2Ok &&
      camsId.trim().length > 0 &&
      camsPwd.trim().length > 0 &&
      kfinId.trim().length > 0 &&
      kfinPwd.trim().length > 0 &&
      startOk &&
      endOk &&
      loginPasswordOk
    );
  }, [
    arnNumber, euin, pan, dob, holderName, gmail, contact1, contact2,
    camsId, camsPwd, kfinId, kfinPwd, validFrom, validTo, loginPassword, confirmLoginPassword, minDOB, today
  ]);

  const onReset = () => {
    setArnNumber(""); setEuin(""); setPan(""); setDob(""); setHolderName("");
    setGmail(""); setContact1(""); setContact2("");
    setCamsId(""); setCamsPwd(""); setKfinId(""); setKfinPwd("");
    setRemarks(""); setLoginPassword(""); setConfirmLoginPassword(""); setIsAdminNew(false);
    const t = toYMD(today); setValidFrom(t); setValidTo(t);
    setContact1Err(false); setContact2Err(false);
  };

  // ⬅️ NEW: Back handler
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/dashboard");
  };

  // ---------- submit ----------
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    const fd = new FormData();
    fd.set("arn_number", `ARN-${arnNumber.trim()}`);
    fd.set("euin", `E-${euin.trim()}`);
    fd.set("pan_number", pan);
    fd.set("dob", dob);
    fd.set("arn_holder_name", holderName.trim());
    fd.set("gmail_id", gmail.trim());
    fd.set("contact_number1", contact1);
    fd.set("contact_number2", contact2);
    fd.set("cams_id", camsId.trim());
    fd.set("cams_password", camsPwd);
    fd.set("kfin_id", kfinId.trim());
    fd.set("kfin_password", kfinPwd);
    fd.set("validity_start", validFrom);
    fd.set("validity_end", validTo);
    fd.set("remarks", remarks.trim());
    fd.set("login_password", loginPassword);
    fd.set("confirm_login_password", confirmLoginPassword);

    // NEW: only if creator is admin, respect the checkbox, else force 0
    fd.set("is_admin", creatorIsAdmin && isAdminNew ? "1" : "0");

    try {
      setSaving(true);
      const authToken = sessionStorage.getItem("distributor_auth_token") || "";
      const res = await fetch(`${API_BASE}/arn_creation.php`, {
        method: "POST",
        headers: {
          "X-KG-Distributor-Authorization": `Bearer ${authToken}`,
        },
        body: fd,
      });
      const data = await res.json();
      if (data?.status === "success") {
        alert(data.message || "✅ ARN Profile saved successfully.");
        onReset();
      } else {
        alert(data?.message || "❌ Error saving ARN Profile.");
      }
    } catch (err) {
      console.error(err);
      alert("❌ Something went wrong. " + err);
    } finally {
      setSaving(false);
    }
  };

  // ---------- UI ----------
  return (
    <div style={{ backgroundColor: "#f0f4ff", minHeight: "100vh", padding: 20 }}>
      <div
        style={{
          backgroundColor: "#fff9db",
          padding: "25px 30px",
          margin: "50px auto",
          borderRadius: 12,
          boxShadow: "0 6px 15px rgba(0,0,0,0.1)",
          maxWidth: 720,
          position: "relative",
        }}
      >
        {/* ⬅️ NEW: Back button at top-left */}
        <button
          type="button"
          onClick={goBack}
          disabled={saving}
          style={btnBackStyle}
          aria-label="Go back"
          title="Go back"
        >
          ← Back
        </button>

        <h2 style={{ textAlign: "center", color: "#333", textDecoration: "underline" }}>
          ARN Profile Setup
        </h2>

        <form onSubmit={onSubmit}>
          {/* Row 1: ARN, EUIN */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: 1 }}>
              <label>ARN Number</label>
              <div style={{ display: "flex" }}>
                <span style={{ padding: "10px", background: "#eee", border: "1px solid #ccc", borderRight: "none", borderRadius: "5px 0 0 5px" }}>ARN-</span>
                <input
                  type="text"
                  value={arnNumber}
                  onChange={(e) => onArnChange(e.target.value)}
                  placeholder="12345678"
                  style={{ padding: 10, border: "1px solid #ccc", borderRadius: "0 5px 5px 0", width: "100%" }}
                />
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <label>EUIN</label>
              <div style={{ display: "flex" }}>
                <span style={{ padding: "10px", background: "#eee", border: "1px solid #ccc", borderRight: "none", borderRadius: "5px 0 0 5px" }}>E-</span>
                <input
                  type="text"
                  value={euin}
                  onChange={(e) => onEuinChange(e.target.value)}
                  placeholder="12345678"
                  style={{ padding: 10, border: "1px solid #ccc", borderRadius: "0 5px 5px 0", width: "100%" }}
                />
              </div>
            </div>
          </div>

          {/* Row 2: PAN, DOB */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 15 }}>
            <div style={{ flex: 1 }}>
              <label>PAN</label>
              <input
                type="text"
                value={pan}
                onChange={(e) => onPanChange(e.target.value)}
                maxLength={10}
                placeholder="ABCDE1234F"
                style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 5 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label>Date of Birth</label>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                onBlur={onDobBlur}
                min={minDOB}
                max={maxDOB}
                style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 5 }}
              />
            </div>
          </div>

          {/* Name */}
          <div style={{ marginTop: 15 }}>
            <label>ARN Holder Name</label>
            <input
              ref={nameRef}
              type="text"
              value={holderName}
              onChange={(e) => onHolderNameChange(e.target.value)}
              placeholder="ARN HOLDER NAME"
              style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 5 }}
            />
          </div>

          {/* Gmail */}
          <div style={{ marginTop: 15 }}>
            <label>Gmail ID</label>
            <input
              type="email"
              value={gmail}
              onChange={(e) => onGmailChange(e.target.value)}
              placeholder="example@gmail.com"
              style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 5 }}
            />
          </div>

          {/* Phones */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 15 }}>
            <div style={{ flex: 1 }}>
              <label>Mobile Number #1</label>
              <input
                type="text"
                value={contact1}
                onChange={(e) => onContact1Change(e.target.value)}
                onBlur={() => onContactBlur(1)}
                placeholder="10-digit number"
                style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 5 }}
              />
              {contact1Err && <span style={{ color: "red" }}>Invalid mobile number</span>}
            </div>
            <div style={{ flex: 1 }}>
              <label>Mobile Number #2</label>
              <input
                type="text"
                value={contact2}
                onChange={(e) => onContact2Change(e.target.value)}
                onBlur={() => onContactBlur(2)}
                placeholder="10-digit number"
                style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 5 }}
              />
              {contact2Err && <span style={{ color: "red" }}>Invalid mobile number</span>}
            </div>
          </div>

          {/* CAMS + password */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 15 }}>
            <div style={{ flex: 1 }}>
              <label>CAMS ID</label>
              <input
                type="text"
                value={camsId}
                onChange={(e) => setCamsId(e.target.value)}
                style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 5 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label>CAMS Password</label>
              <div style={{ position: "relative" }}>
                <input
                  id="cams_password"
                  type="password"
                  value={camsPwd}
                  onChange={(e) => setCamsPwd(e.target.value)}
                  placeholder="Enter password"
                  style={{ width: "100%", padding: "10px 36px 10px 10px", border: "1px solid #ccc", borderRadius: 5 }}
                />
                <span
                  onClick={() => togglePwd("cams_password")}
                  style={{ position: "absolute", top: "50%", right: 10, transform: "translateY(-50%)", cursor: "pointer" }}
                  title="Show/Hide"
                >
                  👁️
                </span>
              </div>
            </div>
          </div>

          {/* KFin + password */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 15 }}>
            <div style={{ flex: 1 }}>
              <label>KFin ID</label>
              <input
                type="text"
                value={kfinId}
                onChange={(e) => setKfinId(e.target.value)}
                style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 5 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label>KFin Password</label>
              <div style={{ position: "relative" }}>
                <input
                  id="kfin_password"
                  type="password"
                  value={kfinPwd}
                  onChange={(e) => setKfinPwd(e.target.value)}
                  placeholder="Enter password"
                  style={{ width: "100%", padding: "10px 36px 10px 10px", border: "1px solid #ccc", borderRadius: 5 }}
                />
                <span
                  onClick={() => togglePwd("kfin_password")}
                  style={{ position: "absolute", top: "50%", right: 10, transform: "translateY(-50%)", cursor: "pointer" }}
                  title="Show/Hide"
                >
                  👁️
                </span>
              </div>
            </div>
          </div>

          {/* Validity */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 15 }}>
            <div style={{ flex: 1 }}>
              <label>Validity Start Date</label>
              <input
                type="date"
                value={validFrom}
                min={toYMD(today)}
                onChange={(e) => setValidFrom(e.target.value)}
                onBlur={onValidFromBlur}
                style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 5 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label>Validity End Date</label>
              <input
                type="date"
                value={validTo}
                min={validFrom || toYMD(today)}
                onFocus={onValidToFocus}
                onChange={(e) => setValidTo(e.target.value)}
                onBlur={onValidToBlur}
                style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 5 }}
              />
            </div>
          </div>

          {/* Login password */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 15 }}>
            <div style={{ flex: 1 }}>
              <label>Login Password</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                autoComplete="new-password"
                minLength={8}
                style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 5 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label>Confirm Login Password</label>
              <input
                type="password"
                value={confirmLoginPassword}
                onChange={(e) => setConfirmLoginPassword(e.target.value)}
                placeholder="Re-enter login password"
                autoComplete="new-password"
                minLength={8}
                style={{ width: "100%", padding: 10, border: `1px solid ${confirmLoginPassword && confirmLoginPassword !== loginPassword ? "#dc2626" : "#ccc"}`, borderRadius: 5 }}
              />
              {confirmLoginPassword && confirmLoginPassword !== loginPassword && (
                <span style={{ color: "#dc2626", fontSize: 12 }}>Passwords do not match</span>
              )}
            </div>
          </div>

          {/* Remarks */}
          <div style={{ marginTop: 15 }}>
            <label>Remarks</label>
            <input
              type="text"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Optional"
              style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 5 }}
            />
          </div>

          {/* NEW: Is Admin checkbox (visible only if creator is admin) */}
          {creatorIsAdmin && (
            <div style={{ marginTop: 15 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={isAdminNew}
                  onChange={(e) => setIsAdminNew(e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                Make this distributor a <u>Master Admin</u>
              </label>
              <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>
                Admins can access admin-only menus (e.g., Scheme Type Mapping, Distributor On Boarding).
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ marginTop: 20 }}>
            <button type="submit" disabled={!isFormValid || saving} style={btnStylePrimary}>
              {saving ? "Saving..." : "Save Data"}
            </button>
            <button type="button" onClick={onReset} style={btnStyleSecondary}>
              Refresh
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const btnStylePrimary = {
  padding: "10px 20px",
  border: "none",
  backgroundColor: "#007BFF",
  color: "white",
  borderRadius: 5,
  cursor: "pointer",
  marginRight: 10,
};

const btnStyleSecondary = {
  padding: "10px 20px",
  border: "none",
  backgroundColor: "#888",
  color: "white",
  borderRadius: 5,
  cursor: "pointer",
};

const btnBackStyle = {
  position: "absolute",
  left: 16,
  top: 16,
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid #d0d7de",
  background: "#fff",
  cursor: "pointer",
  fontSize: 14,
};
